import React, { useState, useEffect } from 'react';
import TopSearchBar from './JobSearch/TopSearchBar';
import JobListingCard from './JobSearch/JobListingCard';
import JobListingCardSkeleton from './JobSearch/JobListingCardSkeleton';
import JobDetailPanel from './JobSearch/JobDetailPanel';
import MobileSearchModal from './JobSearch/MobileSearchModal';
import ClientOnly from './ClientOnly';

interface JobListing {
  id: number;
  title: string;
  max_reward: number | null;
  min_reward: number | null;
  location_name: string;
  created_at: string;
  is_new: boolean;
  contract_type: string;
  workstyle_type: number;
  workstyle_type_label: string | null;
  prefecture: { id: number; name: string } | null;
  city: string | null;
  station: string | null;
  published_at: string;
  skills: Array<{ id: number; name: string }>;
  occupations: Array<{ id: number; name: string }>;
  characteristics: Array<{ id: number; name: string }>;
  job_source: {
    id: number;
    name: string;
    display_name?: string;
    logo_image: string;
    thumbnail_image: string;
  };
  favorited: boolean;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  from: number;
  to: number;
  per_page: number;
}

interface CrossLandingPageProps {
  // 初期データ（SSR用）
  initialJobListings: JobListing[];
  initialPagination: Pagination;
  
  // クロスランディングページ情報
  crossLandingPageId: number;
  displayTitle: string;
  pageTitle: string;
  
  // 現在のソート・ページ
  currentSort: string;
  currentPage: number;
  
  // 検索パラメータ
  searchParams?: {
    skill_ids?: number[];
    occupation_ids?: number[];
    industry_ids?: number[];
    characteristic_ids?: number[];
    prefecture_ids?: number[];
    price_over?: number;
    is_recruiting?: boolean;
    remote_types?: string[];
    workday_types?: string[];
    keyword?: string;
  };
  
  // ユーザー情報
  currentUserId?: number;
  isUserSignedIn: boolean;
  isProfileComplete: boolean;
  
  // SSR フラグ
  isServerRendered?: boolean;
}

const CrossLandingPage: React.FC<CrossLandingPageProps> = ({
  initialJobListings,
  initialPagination,
  crossLandingPageId,
  displayTitle,
  pageTitle,
  currentSort,
  currentPage,
  searchParams = {},
  currentUserId,
  isUserSignedIn,
  isProfileComplete,
  isServerRendered = false
}) => {
  const [jobListings, setJobListings] = useState<JobListing[]>(initialJobListings);
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [sort, setSort] = useState<string>(currentSort);
  const [page, setPage] = useState<number>(currentPage);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJobDetail, setSelectedJobDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMobileSearchModal, setShowMobileSearchModal] = useState(false);

  // ページ変更時の処理
  const handlePageChange = (newPage: number) => {
    if (newPage === page || newPage < 1 || newPage > pagination.total_pages) return;

    setIsLoading(true);
    setPage(newPage);

    const params: string[] = [];
    params.push(`page=${newPage}`);
    if (sort) {
      params.push(`sort=${encodeURIComponent(sort)}`);
    }

    fetch(`/job_listings/cross/${crossLandingPageId}?${params.join('&')}`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        setJobListings(data.job_listings || []);
        setPagination(data.pagination || initialPagination);
        setIsLoading(false);
        
        // スクロール位置を調整
        setTimeout(() => {
          const element = document.querySelector('[data-job-listings]');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 200);
      })
      .catch(error => {
        console.error('Failed to fetch job listings:', error);
        setIsLoading(false);
      });
  };

  // ソート変更時の処理
  const handleSortChange = (newSort: string) => {
    if (newSort === sort) return;

    setIsLoading(true);
    setSort(newSort);
    setPage(1);

    const params: string[] = [];
    params.push(`sort=${encodeURIComponent(newSort)}`);
    params.push('page=1');

    fetch(`/job_listings/cross/${crossLandingPageId}?${params.join('&')}`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        setJobListings(data.job_listings || []);
        setPagination(data.pagination || initialPagination);
        setIsLoading(false);
        
        // スクロール位置を調整
        setTimeout(() => {
          const element = document.querySelector('[data-job-listings]');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 200);
      })
      .catch(error => {
        console.error('Failed to fetch job listings:', error);
        setIsLoading(false);
      });
  };

  // 求人選択時の処理
  const handleJobSelect = (jobId: number) => {
    setSelectedJobId(jobId);
    
    fetch(`/api/v1/job_listings/${jobId}`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        setSelectedJobDetail(data);
      })
      .catch(error => {
        console.error('Failed to fetch job detail:', error);
      });
  };

  const handleJobClose = () => {
    setSelectedJobId(null);
    setSelectedJobDetail(null);
  };

  // ページネーション用のリンク生成
  const generatePageUrl = (pageNum: number) => {
    // SSR環境ではURLSearchParamsが使えないため、文字列連結でURLを生成
    const params: string[] = [];
    params.push(`page=${pageNum}`);
    if (sort) {
      params.push(`sort=${encodeURIComponent(sort)}`);
    }
    return `/job_listings/cross/${crossLandingPageId}?${params.join('&')}`;
  };

  return (
    <div>
      {/* トップ検索バー - ClientOnlyでラップしてhydration errorを回避 */}
      <ClientOnly>
        <TopSearchBar 
          keyword={searchParams?.keyword || ''}
          prefectureIds={Array.isArray(searchParams?.prefecture_ids) ? searchParams.prefecture_ids : []}
          priceOver={searchParams?.price_over}
          isRecruiting={searchParams?.is_recruiting || false}
          remoteTypes={Array.isArray(searchParams?.remote_types) ? searchParams.remote_types : []}
          skillIds={Array.isArray(searchParams?.skill_ids) ? searchParams.skill_ids : []}
          occupationIds={Array.isArray(searchParams?.occupation_ids) ? searchParams.occupation_ids : []}
          industryIds={Array.isArray(searchParams?.industry_ids) ? searchParams.industry_ids : []}
          workdayTypes={Array.isArray(searchParams?.workday_types) ? searchParams.workday_types : []}
          characteristicIds={Array.isArray(searchParams?.characteristic_ids) ? searchParams.characteristic_ids : []}
          jobSourceIds={[]}
          onSearch={(filters) => {
            // SSR時はスキップ
            if (typeof window === 'undefined') return;
            
            // クロスランディングページの検索パラメータを引き継いで検索ページにリダイレクト
            const searchParamsArray: string[] = [];
            
            // 既存の検索パラメータを追加
            if (searchParams.skill_ids?.length) {
              searchParams.skill_ids.forEach(id => searchParamsArray.push(`skill_ids[]=${id}`));
            }
            if (searchParams.occupation_ids?.length) {
              searchParams.occupation_ids.forEach(id => searchParamsArray.push(`occupation_ids[]=${id}`));
            }
            if (searchParams.industry_ids?.length) {
              searchParams.industry_ids.forEach(id => searchParamsArray.push(`industry_ids[]=${id}`));
            }
            if (searchParams.characteristic_ids?.length) {
              searchParams.characteristic_ids.forEach(id => searchParamsArray.push(`characteristic_ids[]=${id}`));
            }
            
            // 追加のフィルターがあれば追加
            if (filters.keyword) searchParamsArray.push(`keyword=${encodeURIComponent(filters.keyword)}`);
            if (filters.prefecture_ids?.length) {
              filters.prefecture_ids.forEach(id => searchParamsArray.push(`prefecture_ids[]=${id}`));
            }
            if (filters.price_over) searchParamsArray.push(`price_over=${filters.price_over}`);
            if (filters.is_recruiting) searchParamsArray.push('is_recruiting=true');
            if (filters.remote_types?.length) {
              filters.remote_types.forEach(type => searchParamsArray.push(`remote_types[]=${encodeURIComponent(type)}`));
            }
            if (filters.workday_types?.length) {
              filters.workday_types.forEach(type => searchParamsArray.push(`workday_types[]=${encodeURIComponent(type)}`));
            }
            if (filters.characteristic_ids?.length) {
              filters.characteristic_ids.forEach(id => searchParamsArray.push(`characteristic_ids[]=${id}`));
            }
            
            // 他のタクソノミーも追加（重複を避ける）
            if (filters.skill_ids?.length) {
              filters.skill_ids.forEach(id => {
                if (!searchParams.skill_ids?.includes(id)) {
                  searchParamsArray.push(`skill_ids[]=${id}`);
                }
              });
            }
            if (filters.occupation_ids?.length) {
              filters.occupation_ids.forEach(id => {
                if (!searchParams.occupation_ids?.includes(id)) {
                  searchParamsArray.push(`occupation_ids[]=${id}`);
                }
              });
            }
            if (filters.industry_ids?.length) {
              filters.industry_ids.forEach(id => {
                if (!searchParams.industry_ids?.includes(id)) {
                  searchParamsArray.push(`industry_ids[]=${id}`);
                }
              });
            }
            
            const searchUrl = `/job_listings/search?${searchParamsArray.join('&')}`;
            window.location.href = searchUrl;
          }}
        />
      </ClientOnly>
      
      <div className="container-fluid">
        <div className="row g-0 mt-0">
          {/* 左カラム: 求人一覧 */}
          <div className="col-12 col-md-4 d-flex flex-column" data-job-listings="true">
            {/* ページヘッダー(1ページ目のみ) */}
            {currentPage === 1 && (
              <div className="mb-4">
                <h1 className="h3 fw-bold mb-2">{displayTitle}</h1>
                <p className="text-muted small mb-2 projects-update-date">
                  最終更新日：{new Date().toLocaleDateString('ja-JP', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    weekday: 'short'
                  })}
                </p>
                <p className="text-muted mb-3">
                  {pagination.total_count > 0
                    ? `${pagination.from}〜${pagination.to}件目 / 全${pagination.total_count}件`
                    : `全${pagination.total_count}件`}
                </p>
              </div>
            )}
            
            {/* 求人一覧 */}
            <div className="bg-white rounded shadow-sm p-4 mb-4 flex-grow-1">
              {/* ヘッダー: タイトル、ソート、検索ボタン */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="h5 fw-bold mb-0">求人一覧</h2>
                <div className="d-flex gap-2">
                  {/* ソートドロップダウン */}
                  {jobListings.length > 0 && (
                    <select 
                      value={sort} 
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                    >
                      <option value="created_at desc">最新順</option>
                      <option value="max_reward desc">高単価順</option>
                    </select>
                  )}
                  
                  {/* 詳細検索ボタン */}
                  <a 
                    href={`/job_listings/search?${Object.entries(searchParams).flatMap(([key, value]) => {
                      if (Array.isArray(value)) {
                        return value.map(v => `${key}[]=${v}`);
                      } else if (value !== undefined && value !== null) {
                        return [`${key}=${encodeURIComponent(value.toString())}`];
                      }
                      return [];
                    }).join('&')}`}
                    className="btn btn-primary btn-sm"
                  >
                    <i className="bi bi-sliders me-1"></i>条件を変更して検索
                  </a>
                </div>
              </div>
              
              {/* 件数表示 */}
              {pagination.total_count > 0 && (
                <div className="mb-3">
                  <p className="text-muted small mb-0">
                    {pagination.from}〜{pagination.to}件目 / 全{pagination.total_count}件
                  </p>
                </div>
              )}
              
              {/* 求人一覧 or 空状態 */}
              {isLoading ? (
                <div className="row">
                  {Array.from({ length: Math.max(jobListings.length, 5) }).map((_, index) => (
                    <div key={`skeleton-${index}`} className="col-12 mb-3">
                      <JobListingCardSkeleton />
                    </div>
                  ))}
                </div>
              ) : jobListings.length > 0 ? (
                <>
                  <div className="row">
                    {jobListings.map((job) => (
                      <div key={job.id} className="col-12 mb-3">
                        <JobListingCard
                          jobListing={{
                            ...job,
                            is_favorited: job.favorited,
                            skills: job.skills,
                            prefecture: job.prefecture,
                            location_name: job.location_name
                          }}
                          onClick={() => handleJobSelect(job.id)}
                          isSelected={selectedJobId === job.id}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* ページネーション */}
                  {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <nav aria-label="Page navigation">
                        <ul className="pagination">
                          {pagination.current_page > 1 && (
                            <li className="page-item">
                              <a
                                className="page-link"
                                href={generatePageUrl(pagination.current_page - 1)}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(pagination.current_page - 1);
                                }}
                              >
                                前へ
                              </a>
                            </li>
                          )}
                          
                          {[...Array(pagination.total_pages)].map((_, i) => {
                            const pageNum = i + 1;
                            if (
                              pageNum === 1 ||
                              pageNum === pagination.total_pages ||
                              (pageNum >= pagination.current_page - 2 && pageNum <= pagination.current_page + 2)
                            ) {
                              return (
                                <li
                                  key={pageNum}
                                  className={`page-item ${pageNum === pagination.current_page ? 'active' : ''}`}
                                >
                                  <a
                                    className="page-link"
                                    href={generatePageUrl(pageNum)}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handlePageChange(pageNum);
                                    }}
                                  >
                                    {pageNum}
                                  </a>
                                </li>
                              );
                            } else if (
                              pageNum === pagination.current_page - 3 ||
                              pageNum === pagination.current_page + 3
                            ) {
                              return (
                                <li key={pageNum} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }
                            return null;
                          })}
                          
                          {pagination.current_page < pagination.total_pages && (
                            <li className="page-item">
                              <a
                                className="page-link"
                                href={generatePageUrl(pagination.current_page + 1)}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(pagination.current_page + 1);
                                }}
                              >
                                次へ
                              </a>
                            </li>
                          )}
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-search fs-1 text-muted mb-3 d-block"></i>
                  <h6 className="mb-2">該当する案件が見つかりませんでした</h6>
                  <p className="text-muted small">条件を変更して再度お試しください。</p>
                </div>
              )}
            </div>
          </div>
          
          {/* 右カラム: 求人詳細 + SEOコンテンツ */}
          <div className="col-md-8 col-lg-8 d-none d-md-block">
            <ClientOnly>
              <JobDetailPanel
                jobDetail={selectedJobDetail}
                onClose={handleJobClose}
                currentUserId={currentUserId}
                isUserSignedIn={isUserSignedIn}
                isProfileComplete={isProfileComplete}
                isLoading={isLoading}
              />
            </ClientOnly>
          </div>
        </div>
      </div>

      {/* スマホ用検索条件モーダル */}
      <ClientOnly>
        <MobileSearchModal
          show={showMobileSearchModal}
          onClose={() => setShowMobileSearchModal(false)}
          onSearch={(searchFilters) => {
            // クロスランディングページの検索パラメータを引き継いで検索ページにリダイレクト
            const searchParamsArray: string[] = [];
            
            // 既存の検索パラメータを追加
            if (searchParams.skill_ids?.length) {
              searchParams.skill_ids.forEach(id => searchParamsArray.push(`skill_ids[]=${id}`));
            }
            if (searchParams.occupation_ids?.length) {
              searchParams.occupation_ids.forEach(id => searchParamsArray.push(`occupation_ids[]=${id}`));
            }
            if (searchParams.industry_ids?.length) {
              searchParams.industry_ids.forEach(id => searchParamsArray.push(`industry_ids[]=${id}`));
            }
            if (searchParams.characteristic_ids?.length) {
              searchParams.characteristic_ids.forEach(id => searchParamsArray.push(`characteristic_ids[]=${id}`));
            }
            
            // 検索フィルターを追加
            if (searchFilters.keyword) searchParamsArray.push(`keyword=${encodeURIComponent(searchFilters.keyword)}`);
            searchFilters.prefecture_ids.forEach(id => searchParamsArray.push(`prefecture_ids[]=${id}`));
            if (searchFilters.price_over) searchParamsArray.push(`price_over=${searchFilters.price_over}`);
            if (searchFilters.is_recruiting) searchParamsArray.push('is_recruiting=true');
            searchFilters.remote_types.forEach(type => searchParamsArray.push(`remote_types[]=${encodeURIComponent(type)}`));
            searchFilters.workday_types.forEach(type => searchParamsArray.push(`workday_types[]=${encodeURIComponent(type)}`));
            searchFilters.characteristic_ids.forEach(id => searchParamsArray.push(`characteristic_ids[]=${id}`));
            
            // 他のタクソノミーも追加（重複を避ける）
            searchFilters.skill_ids.forEach(id => {
              if (!searchParams.skill_ids?.includes(id)) {
                searchParamsArray.push(`skill_ids[]=${id}`);
              }
            });
            searchFilters.occupation_ids.forEach(id => {
              if (!searchParams.occupation_ids?.includes(id)) {
                searchParamsArray.push(`occupation_ids[]=${id}`);
              }
            });
            searchFilters.industry_ids.forEach(id => {
              if (!searchParams.industry_ids?.includes(id)) {
                searchParamsArray.push(`industry_ids[]=${id}`);
              }
            });
            
            const searchUrl = `/job_listings/search?${searchParamsArray.join('&')}`;
            window.location.href = searchUrl;
          }}
          initialFilters={{
            keyword: searchParams?.keyword || '',
            prefecture_ids: Array.isArray(searchParams?.prefecture_ids) ? searchParams.prefecture_ids : [],
            price_over: searchParams?.price_over,
            is_recruiting: searchParams?.is_recruiting || false,
            remote_types: Array.isArray(searchParams?.remote_types) ? searchParams.remote_types : [],
            skill_ids: Array.isArray(searchParams?.skill_ids) ? searchParams.skill_ids : [],
            occupation_ids: Array.isArray(searchParams?.occupation_ids) ? searchParams.occupation_ids : [],
            industry_ids: Array.isArray(searchParams?.industry_ids) ? searchParams.industry_ids : [],
            workday_types: Array.isArray(searchParams?.workday_types) ? searchParams.workday_types : [],
            characteristic_ids: Array.isArray(searchParams?.characteristic_ids) ? searchParams.characteristic_ids : [],
            job_source_ids: []
          }}
        />
      </ClientOnly>

    </div>
  );
};

export default CrossLandingPage;
